<template>
    <div v-if="canShow" class="SuggestionContainer" @click="apply()">
		<div class="SuggestionContent">
			<Chip
				:label="filterSelection?.name || ''"
				:icon="chipIcon"
			/>
			<span class="ShowResults" @click="apply($event)">{{ resultsMessage }}</span>
		</div>
    </div>
</template>

<script setup lang="ts">
import Chip from '@app/components/TagsElement/Chip.vue';
import { computed, PropType } from 'vue';
import { useSearchStore } from '@app/stores/useSearchStore';
import { ILowResultSuggestionResult } from '@domain/interfaces/dtos/ILowResultSuggestion';
import IExtendedSearchQueryFilter from '@app/interfaces/dtos/IExtendedSearchQueryFilter';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { LOW_RESULTS_ICON_CHIP_MAPPER } from '@domain/mappers/LowResultsFilterTypeMapper';

const searchStore = useSearchStore();
const { filterSelection } = defineProps({
	filterSelection: {
		type: Object as PropType<ILowResultSuggestionResult>,
		required: true
	}
});


const canShow = computed(() => !!filterSelection?.name && filterSelection.countValues && filterSelection.countValues > 0);
const chipIcon = computed(()=> LOW_RESULTS_ICON_CHIP_MAPPER.get(filterSelection?.type || ''));
const ELIGIBLE_FILTER_TYPES = [FilterType.DISCIPLINES, FilterType.COUNTRY, FilterType.CONTINENT];
const ALL_AROUND_THE_WORLD_SUGGESTION_ID = "all-around-the-world";

const resultsMessage = computed(() => {
	if (!filterSelection?.countValues) return '';

	return `Show ${filterSelection.countValues} ${filterSelection.countValues === 1 ? 'result' : 'results'}`;
});

const setFiltersSelection = ({type, ...rest}: ILowResultSuggestionResult): IExtendedSearchQueryFilter[] => {
	let nextFilters = searchStore.filtersSelection.filter((f) => f.type !== type && !rest?.removeType?.includes(f.type));

	if(ELIGIBLE_FILTER_TYPES.includes(type) && rest.id !== ALL_AROUND_THE_WORLD_SUGGESTION_ID) {
		nextFilters = [...nextFilters, { type, values: [{...rest}] }];
	}

	return nextFilters;
}

const handleSelection = async (filter: ILowResultSuggestionResult): Promise<void> => {
	const nextFilters = setFiltersSelection(filter);

	searchStore.filtersSelection = nextFilters as IExtendedSearchQueryFilter[];

	await searchStore.setRouteAndLoadData();
};


const apply = async (event?: Event) => {
    if (event) event.stopPropagation();

	return await handleSelection(filterSelection);
}
</script>

<style scoped lang="scss">
.SuggestionContainer {
	display: flex;
	background-color: $White;
	@include TextStyle(HeadingExtraSmall);
	@include PaddingAreas(1);

	&:not(:last-child) {
		border-bottom: 1px solid $Grey-200;
	}

	.SuggestionContent {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		cursor: pointer;

		.Chip {
			width: fit-content;
		}

	}

	.ShowResults {
		cursor: pointer;
		color: $Blue-700;

		@include TextStyle(BodyRegular);

		&:hover {
			text-decoration: underline;
		}
	}

}


</style>